<template>
  <div class="kiosk-join-form w-full sm:w-2/3 xl:w-2/5">
    <div class="vx-card kiosk-form-card p-0 sm:p-8">
      <!-- Back button -->
      <button
        type="button"
        class="kiosk-back-btn flex items-center text-content mb-4"
        @click="$emit('back')"
      >
        <feather-icon icon="ArrowLeftIcon" svgClasses="h-5 w-5 mr-2" />
        <span class="text-sm font-medium">Back</span>
      </button>

      <h2 class="text-xl md:text-2xl font-bold text-dark mb-2">Enter your details</h2>
      <p class="text-sm text-content mb-6">Please fill out the form below to secure your spot.</p>

      <form @submit.prevent>
        <!-- Name fields -->
        <div class="flex flex-row gap-x-4">
          <div class="w-1/2">
            <vs-input
              v-model.trim="firstName"
              class="w-full required"
              :label="$t('$General.FirstName')"
              :name="$t('$General.FirstName')"
              autocomplete="given-name"
              v-validate="'required|max:50'"
              data-vv-validate-on="blur|input"
              :danger="errors.has($t('$General.FirstName'))"
              :danger-text="errors.first($t('$General.FirstName'))"
              val-icon-danger="clear"
            />
          </div>
          <div class="w-1/2">
            <vs-input
              v-model.trim="lastName"
              class="w-full"
              :label="$t('$General.LastName')"
              :name="$t('$General.LastName')"
              autocomplete="family-name"
              v-validate="'max:50'"
              data-vv-validate-on="blur|input"
              :danger="errors.has($t('$General.LastName'))"
              :danger-text="errors.first($t('$General.LastName'))"
              val-icon-danger="clear"
            />
          </div>
        </div>

        <!-- Phone number -->
        <div class="mt-6">
          <label class="vs-input--label required">
            {{ $tc('$General.Phone') }}
          </label>
          <vue-tel-input
            ref="telInput"
            v-model="phoneNational"
            :disabledFetchingCountry="true"
            :dynamicPlaceholder="false"
            :enabledCountryCode="true"
            :validCharactersOnly="true"
            :preferredCountries="['AU', 'CO']"
            defaultCountry="AU"
            autocomplete="tel"
            @validate="(payload) => (phoneValidate = payload)"
            @input="() => (phoneInput = true)"
            @blur="() => (phoneInput = true)"
          />
          <span v-show="invalidPhoneNumber" class="text-danger text-sm">
            {{ $t('$ContactModule.InvalidPhoneNumberMsg') }}
          </span>
          <span v-show="requiredPhoneNumber" class="text-danger text-sm">
            {{ $t('$ContactModule.PhoneNumberRequiredMsg') }}
          </span>
        </div>

        <!-- Email -->
        <div class="mt-6">
          <vs-input
            v-model.trim="email"
            type="email"
            class="w-full"
            :label="`${$tc('$General.Email')} (Optional)`"
            :name="$tc('$General.Email')"
            autocomplete="email"
            v-validate="{ rules: { email: true } }"
            data-vv-validate-on="blur|input"
            :danger="errors.has($tc('$General.Email'))"
            :danger-text="errors.first($tc('$General.Email'))"
            val-icon-danger="clear"
          />
        </div>

        <!-- Opt-in -->
        <vs-divider />

        <div class="vx-row">
          <div class="vx-col w-full">
            <vs-checkbox
              v-model="optInAgreement"
              style="display: -webkit-inline-box !important; -webkit-box-align: start;"
            >
              Join our list for special offers and updates.
            </vs-checkbox>
          </div>
          <div class="vx-col w-full mt-5 text-justify md:text-left">
            <small class="mt-3">
              By securing a spot, you agree to receive queue communications and accept our
              <a
                v-if="policyType === $enums.Tenants.POLICY_TYPE.URL"
                :href="linkPolicy"
                target="_blank"
                class="link-contextual privacy-policy-link text-gray-dark"
              >
                privacy policy</a><router-link
                v-else
                :to="`/privacy-policy/${tenantUrlLinksSuffix}`"
                target="_blank"
                class="link-contextual font-bold privacy-policy-link text-gray-dark"
              >privacy policy</router-link>.
            </small>
          </div>
        </div>

        <vs-divider />

        <!-- Submit button -->
        <vs-button
          :style="`background: ${accentColour} !important; color: ${accentContrastTextColor} !important`"
          class="kiosk-submit-btn w-full shadow-md"
          @click="secureSpot"
        >
          Secure spot
        </vs-button>
      </form>

      <!-- Continue on your phone -->
      <vs-divider class="mt-8" />

      <div class="text-center mt-6">
        <div class="flex items-center justify-center mb-3">
          <feather-icon icon="SmartphoneIcon" svgClasses="h-4 w-4 mr-2 text-content" />
          <span class="text-sm font-semibold text-dark">Continue on your phone</span>
        </div>
        <div class="kiosk-qr-frame-small mx-auto mb-2">
          <qrcode-vue
            :value="waitlistFormUrl"
            :size="120"
            level="H"
            render-as="canvas"
            class="flex justify-center"
          />
        </div>
        <p class="text-xs text-content">Scan to open waitlist on your device</p>
      </div>
    </div>
  </div>
</template>

<script>
import { VueTelInput } from 'vue-tel-input';
import QrcodeVue from 'qrcode.vue';
import { getContrastColor } from '@/utils/colorUtils';
import waitingListService from '@/api/modules/waiting-list.service';

export default {
  name: 'KioskJoinForm',
  components: {
    VueTelInput,
    QrcodeVue,
  },
  props: {
    accentColour: {
      type: String,
      required: true,
    },
    tenantUrlLinksSuffix: {
      type: String,
      required: true,
    },
    policyType: {
      type: String,
      default: '',
    },
    linkPolicy: {
      type: String,
      default: '',
    },
    waitlistFormUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phoneNational: '',
      phoneValidate: { isValid: false },
      phoneInput: false,
      optInAgreement: false,
    };
  },
  computed: {
    accentContrastTextColor() {
      return this.accentColour ? getContrastColor(this.accentColour) : '#ffffff';
    },
    invalidPhoneNumber() {
      return this.phoneInput && !this.phoneValidate.isValid && this.phoneNational !== '';
    },
    requiredPhoneNumber() {
      return this.phoneInput && !this.phoneValidate.isValid && this.phoneNational === '';
    },
    verifyProperties() {
      return this.firstName !== '' && (this.phoneNational !== '' && this.phoneValidate.isValid);
    },
  },
  methods: {
    getPhoneInternationalE164() {
      if (this.phoneValidate && this.phoneValidate.number && this.phoneValidate.number.e164) {
        return this.phoneValidate.number.e164;
      }
      return '';
    },
    getPhoneNational() {
      if (this.phoneValidate && this.phoneValidate.number && this.phoneValidate.number.national) {
        return this.phoneValidate.number.national;
      }
      return '';
    },
    async secureSpot() {
      this.phoneInput = true;
      const formIsValid = await this.$validator.validate();

      if (this.verifyProperties && formIsValid) {
        this.$vs.loading({
          type: 'radius',
          color: this.accentColour,
        });

        try {
          const response = await waitingListService.bookTurnInWaitingList({
            firstName: this.$options.filters.capitalize(this.firstName),
            lastName: this.$options.filters.capitalize(this.lastName),
            email: this.email.toLowerCase(),
            phoneInternational: this.getPhoneInternationalE164(),
            subscribed: this.optInAgreement,
          }, this.tenantUrlLinksSuffix);

          const turnInfo = response.data;
          const customerName = [this.firstName, this.lastName].filter(Boolean).join(' ').trim();

          this.$emit('booked', {
            turnInfo,
            customerName,
            customerPhone: this.getPhoneNational(),
            customerEmail: this.email,
          });

          // Reset form for next customer
          this.resetForm();
        } catch (err) {
          // Show error to user
          this.$vs.notify({
            title: 'Error',
            text: 'Could not secure your spot. Please try again.',
            color: 'danger',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
          });
        } finally {
          this.$vs.loading.close();
        }
      }
    },
    resetForm() {
      this.firstName = '';
      this.lastName = '';
      this.email = '';
      this.phoneNational = '';
      this.phoneValidate = { isValid: false };
      this.phoneInput = false;
      this.optInAgreement = false;
      this.$validator.reset();
    },
  },
};
</script>

<style lang="scss" scoped>
.kiosk-join-form {
  animation: slideUp 0.6s ease;
}

.kiosk-form-card {
  border-radius: 16px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: v-bind('accentColour');
    z-index: 1;
  }

  // Mobile: hide card styling
  @media (max-width: 575.98px) {
    &.vx-card {
      background: transparent;
      border: none;
      box-shadow: none;

      &::before {
        display: none;
      }
    }
  }
}

.kiosk-back-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 0;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
}

.kiosk-submit-btn {
  transition: all 0.2s ease !important;

  &:hover {
    border: 1px solid v-bind('accentColour') !important;
    box-shadow: 0 8px 25px -8px v-bind('accentColour') !important;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 15px -6px v-bind('accentColour') !important;
  }
}

.kiosk-qr-frame-small {
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 12px;
  display: inline-block;
}

// Privacy policy link styling
.privacy-policy-link {
  color: rgba(var(--vs-grey-dark), 1) !important;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
}

@keyframes slideUp {
  0% {
    transform: translateY(20px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

<style lang="scss">
/* Unscoped styles for vue-tel-input and vs-input theming within kiosk form */
.kiosk-join-form {
  .vue-tel-input {
    border: 1px solid rgba(0, 0, 0, 0.2) !important;
    border-radius: 5px !important;
    transition: all 0.15s ease !important;

    .vti__input {
      border: none !important;
      outline: none !important;
      padding: 10px 12px !important;
      font-size: 0.875rem !important;
    }

    .vti__dropdown {
      border-right: 1px solid rgba(0, 0, 0, 0.1) !important;
      background: transparent !important;

      &:hover {
        background: rgba(0, 0, 0, 0.02) !important;
      }
    }
  }

  .vti__dropdown-list {
    z-index: 9999 !important;
  }

  .vs-checkbox--check {
    background: v-bind('accentColour') !important;
  }

  .vs-checkbox--icon {
    color: v-bind('accentContrastTextColor') !important;
  }

  .vs-checkbox-primary input:checked + .vs-checkbox {
    border: 2px solid v-bind('accentColour') !important;
    background: v-bind('accentColour') !important;
  }

  .vs-checkbox:hover {
    border-color: v-bind('accentColour') !important;
  }

  .vs-input-primary .vs-input--input:focus {
    border: 1px solid v-bind('accentColour') !important;
  }
}
</style>
